import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Thrown by `open()` when the browser blocked the popup (`window.open`
 * returned `null`). Callers can detect this to show a "please allow pop-ups"
 * message instead of a generic error.
 */
export class PopupBlockedError extends Error {
  constructor() {
    super('Popup was blocked by the browser');
    this.name = 'PopupBlockedError';
  }
}

interface UseExternalPopupArgs {
  /**
   * Called once when the user returns from the external window — either because
   * the popup closed (detected by polling / on our window regaining focus) or
   * the user confirmed manually. Use it to refetch and re-evaluate state.
   */
  onReturn: () => void;
  /** `window.open` target name. */
  name?: string;
  /** `window.open` features string (size, etc.). */
  features?: string;
}

const DEFAULT_FEATURES = 'popup=yes,width=480,height=720';
const POLL_INTERVAL_MS = 500;

const noop = () => {};

/**
 * Drives the lifecycle of an external, cross-origin flow opened in a popup
 * window (e.g. the ID Austria / NEWCON page).
 *
 * Because the opened page is third-party and never messages us back,
 * `postMessage` / `BroadcastChannel` / `storage` events are unusable here.
 * Instead we detect that the popup has closed entirely from our side:
 *   1. polling `popup.closed` (~500ms),
 *   2. re-checking `popup.closed` when our window regains focus / visibility
 *      (an instant check — we deliberately do NOT finish just because the user
 *      tabbed back while the popup is still open),
 *   3. an explicit `confirmReturn()` (a manual "I've finished" button) which is
 *      the fallback when COOP headers make `popup.closed` unreliable.
 *
 * `open()` opens a blank popup *synchronously* (preserving the click's user
 * activation so it isn't blocked), severs the opener (anti reverse-tabnabbing),
 * then navigates it to the awaited URL.
 */
export const useExternalPopup = ({
  onReturn,
  name = 'externalProvider',
  features = DEFAULT_FEATURES,
}: UseExternalPopupArgs) => {
  const [isOpen, setIsOpen] = useState(false);

  const popupRef = useRef<Window | null>(null);
  const finishedRef = useRef(false);
  const teardownRef = useRef<() => void>(noop);

  // Keep the latest onReturn without re-creating callbacks / listeners.
  const onReturnRef = useRef(onReturn);
  onReturnRef.current = onReturn;

  const finish = useCallback(
    ({ invokeReturn, closePopup }: { invokeReturn: boolean; closePopup: boolean }) => {
      if (finishedRef.current) return;
      finishedRef.current = true;

      teardownRef.current();
      teardownRef.current = noop;

      if (closePopup && popupRef.current) {
        try {
          // `.closed` read + `.close()` can throw under some COOP setups; keep both guarded.
          if (!popupRef.current.closed) popupRef.current.close();
        } catch {
          // ignore
        }
      }
      popupRef.current = null;
      setIsOpen(false);

      if (invokeReturn) onReturnRef.current();
    },
    [],
  );

  const open = useCallback(
    async (getUrl: () => Promise<string>) => {
      // A flow is already active — surface the existing popup instead of spawning another.
      if (popupRef.current && !finishedRef.current) {
        try {
          popupRef.current.focus();
        } catch {
          // ignore
        }
        return;
      }

      // Open synchronously to keep the user activation (avoids popup blockers).
      const popup = window.open('', name, features);
      if (!popup) throw new PopupBlockedError();

      // Sever the opener while the popup is still about:blank so the cross-origin
      // page can't reach back into our window (reverse tabnabbing). Our handle still
      // works for `.closed` polling and navigation.
      try {
        popup.opener = null;
      } catch {
        // ignore
      }

      popupRef.current = popup;
      finishedRef.current = false;
      setIsOpen(true);

      const finishIfClosed = () => {
        try {
          if (popupRef.current?.closed) finish({ invokeReturn: true, closePopup: false });
        } catch {
          // Reading `.closed` can throw under COOP; the manual fallback covers that.
        }
      };
      const onFocus = () => finishIfClosed();
      const onVisibility = () => {
        if (document.visibilityState === 'visible') finishIfClosed();
      };

      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVisibility);
      const pollId = window.setInterval(finishIfClosed, POLL_INTERVAL_MS);

      teardownRef.current = () => {
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('visibilitychange', onVisibility);
        window.clearInterval(pollId);
      };

      try {
        const url = await getUrl();
        try {
          if (popupRef.current && !popupRef.current.closed) {
            popupRef.current.location.href = url;
          }
        } catch {
          // Reading `.closed` / navigating can throw under COOP; poll + manual handle return.
        }
      } catch (error) {
        // Couldn't build the target URL (e.g. the signed request failed):
        // close the blank popup and reset without treating it as a return.
        finish({ invokeReturn: false, closePopup: true });
        throw error;
      }
    },
    [features, name, finish],
  );

  /** Abort the flow (e.g. a Cancel button): close the popup, no refetch. */
  const close = useCallback(() => finish({ invokeReturn: false, closePopup: true }), [finish]);

  /** Manual fallback ("I've finished"): close the popup and refetch. */
  const confirmReturn = useCallback(
    () => finish({ invokeReturn: true, closePopup: true }),
    [finish],
  );

  // Clean up listeners / popup if the component unmounts mid-flow.
  useEffect(
    () => () => {
      teardownRef.current();
      teardownRef.current = noop;
      if (popupRef.current) {
        try {
          if (!popupRef.current.closed) popupRef.current.close();
        } catch {
          // ignore
        }
      }
      popupRef.current = null;
    },
    [],
  );

  return { isOpen, open, close, confirmReturn };
};
